// add spell check
// Move "print all notes" into Previous notes

import React, {useState, useEffect, useRef, useMemo, useContext} from 'react';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Autosave,
  BalloonToolbar,
  Bold,
  Essentials,
  FontColor,
  Heading,
  Highlight,
  Italic,
  List,
  ListProperties,
  Paragraph,
  Underline,
  WordCount,
  EditorConfig,
  ToolbarConfigItem,
  Link,
} from 'ckeditor5';

// eslint-disable-next-line import/no-unresolved
import 'ckeditor5/ckeditor5.css';
import TimeAgo from 'timeago-react';
import {Box, Typography} from '@mui/material';
import {isDate} from 'lodash-es';
import {themeColors} from '../../theme/Theme';
import {FormReadOnlyContext} from '../AutoForm/AutoFormNew';
import {ShowIfDebugWrapper} from '../../contexts/DebugProvider';

const LICENSE_KEY = 'GPL';

export const defaultRichEditorToolbarItems: ToolbarConfigItem[] = [
  'heading',
  '|',
  'fontSize',
  'fontFamily',
  'fontColor',
  'fontBackgroundColor',
  '|',
  'bold',
  'italic',
  'underline',
  '|',
  'link',
  'highlight',
  '|',
  'bulletedList',
  'numberedList',
  'todoList',
];

export const clientPortalSafeToolbarItems: ToolbarConfigItem[] = [
  'heading',
  '|',
  'fontSize',
  'fontFamily',
  // 'fontColor',
  // 'fontBackgroundColor',
  '|',
  'bold',
  'italic',
  'underline',
  '|',
  // 'link',
  // 'highlight',
  '|',
  'bulletedList',
  'numberedList',
  'todoList',
];

export const RichEditor: React.FC<{
  formikId: string;
  placeholder: string;
  onSave: (val: string) => Promise<any>;
  charLimit?: number;
  initialValue?: string;
  disabled?: boolean;
  error?: string;
  toolbarItems?: ToolbarConfigItem[];
}> = ({
  formikId,
  onSave,
  error,
  placeholder,
  charLimit = 5000,
  initialValue,
  disabled,
  toolbarItems = defaultRichEditorToolbarItems,
}) => {
  const [characterCount, setCharacterCount] = useState(0);
  const {readOnly: readOnlyContext, emptyValText} = useContext(FormReadOnlyContext);
  const editorContainerRef = useRef(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editorWordCountRef = useRef<HTMLDivElement | null>(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [savingState, setSaving] = useState<'saving' | Date | 'not edited'>('not edited');
  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  const overLimit = charLimit && characterCount > charLimit;
  const config: {editorConfig: EditorConfig} | null = useMemo(() => {
    if (!isLayoutReady) {
      return null;
    }

    const editorConfig: EditorConfig = {
      toolbar: {
        items: toolbarItems,
        shouldNotGroupWhenFull: false,
      },
      label: placeholder,

      plugins: [
        Autosave,
        BalloonToolbar,
        Bold,
        Essentials,
        FontColor,
        Heading,
        Highlight,
        Italic,
        List,
        Link,
        ListProperties,
        Paragraph,
        Underline,
        WordCount,
      ],
      balloonToolbar: [
        'bold',
        'italic',
        '|',
        'link',
        '|',
        'bulletedList',
        'numberedList',
      ],
      fontFamily: {
        supportAllValues: true,
      },
      fontSize: {
        options: [10, 12, 14, 'default', 18, 20, 22],
        supportAllValues: true,
      },
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph',
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1',
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2',
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3',
          },
          {
            model: 'heading4',
            view: 'h4',
            title: 'Heading 4',
            class: 'ck-heading_heading4',
          },
          {
            model: 'heading5',
            view: 'h5',
            title: 'Heading 5',
            class: 'ck-heading_heading5',
          },
          {
            model: 'heading6',
            view: 'h6',
            title: 'Heading 6',
            class: 'ck-heading_heading6',
          },
        ],
      },
      licenseKey: LICENSE_KEY,
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file',
            },
          },
        },
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true,
        },
      },
      placeholder,
      wordCount: {
        displayWords: false,
        onUpdate: ({characters}) => {
          setCharacterCount(characters);
        },
      },
      autosave: {
        waitingTime: 1000, // in ms
        save(editor) {
          const newMarkupBody = editor.getData();
          return onSave(newMarkupBody);
        },
      },
    };
    return {editorConfig};
  }, [isLayoutReady, readOnlyContext, disabled]);

  function displayStatus(editor: ClassicEditor) {
    const pendingActions = editor.plugins.get('PendingActions');

    pendingActions.on('change:hasAny', (evt, propertyName, newValue) => {
      if (newValue) {
        setSaving('saving');
      } else {
        setSaving(new Date());
      }
    });
  }

  return (
    <>
      <Box mt={1} className="main-container">
        <ShowIfDebugWrapper>
          <Typography fontSize={12}>{formikId}</Typography>
        </ShowIfDebugWrapper>
        <div
          className="editor-container editor-container_classic-editor editor-container_include-word-count"
          ref={editorContainerRef}
        >
          <div
            data-testid="rich-editor"
            className={`editor-container__editor ${error ? 'error' : ''}`}
          >
            <div ref={editorRef}>
              {config?.editorConfig && (
                <CKEditor
                  onReady={(editor: ClassicEditor) => {
                    const wordCount = editor.plugins.get('WordCount');
                    editorWordCountRef.current?.appendChild(wordCount.wordCountContainer);
                    // if (editor?.ui?.view?.editable?.element) {
                    //   editor.ui.view.editable.element.style.minHeight = '300px';
                    // }
                    displayStatus(editor);
                  }}
                  onAfterDestroy={() => {
                    Array.from(editorWordCountRef?.current?.children || []).forEach(
                      (child) => child.remove()
                    );
                  }}
                  disabled={disabled || readOnlyContext}
                  data={initialValue}
                  editor={ClassicEditor}
                  config={config?.editorConfig}
                />
              )}
            </div>
          </div>
          <Box
            bgcolor={overLimit ? themeColors.lightWarning : undefined}
            display="flex"
            p={2}
            justifyContent="space-between"
          >
            {error ? (
              <Typography variant="h6" color="error">
                {error}
              </Typography>
            ) : null}
            {savingState === 'saving' ? (
              <Box>Saving...</Box>
            ) : isDate(savingState) ? (
              <Box color={themeColors.darkGreen}>
                <b>
                  Last saved: <TimeAgo opts={{minInterval: 30}} datetime={savingState} />
                </b>
              </Box>
            ) : (
              <Box />
            )}
            <Box color={overLimit ? themeColors.red : themeColors.black}>
              Chars: {characterCount}/{charLimit}
            </Box>
          </Box>
        </div>
      </Box>
    </>
  );
};
